/**
 * Notifications repository — the data-access home for the multi-channel
 * notifications subsystem (per-user preferences, per-company channel/event
 * config, the resolved template surface, the per-send delivery log, and the
 * Hatif/WhatsApp availability RPCs).
 *
 * The ONLY place that touches these tables / RPCs:
 *   - `notifications`                        (in-app feed rows; dispatcher-written)
 *   - `notification_preferences`             (per-user pivoted prefs)
 *   - `notification_sends`                   (delivery log; realtime feed)
 *   - `notification_templates`               (platform + company-scoped templates)
 *   - `company_channel_settings`             (super-admin per-company channel on/off)
 *   - `company_notification_event_config`    (company-admin per-event on/off)
 *   - `company_notification_recipients`      (per-company contact per role, e.g. finance)
 *   - `notification_audience_config`         (per event × audience × channel;
 *                                             company tier + platform defaults)
 *   - `get_company_hatif_status` RPC         (Hatif WhatsApp creds status — non-secret)
 *   - `company_available_channels` RPC       (resolved per-channel availability)
 *
 * Strangler-Fig per-slice migration (manifest-6, notifications slice):
 *   Step 1 (HERE) — consolidated scattered `supabase.from(...)` / `.rpc(...)` /
 *                   `.channel(...)` calls from the 7 hooks below. The query
 *                   shapes (table, columns, filters, conflict keys, realtime
 *                   filter) are BYTE-IDENTICAL to the inline originals so the
 *                   trigger flow (dispatcher writes the inapp row; hook reads
 *                   it) is preserved.
 *   Step 2 (deferred) — there are NO Nest endpoints for these tables yet, so
 *                   every method is Supabase-only. No `makeNestRequest` /
 *                   `API_ROUTES` plumbing.
 *
 * Consumer hooks (every `.from(...)` / `.rpc(...)` / `.channel(...)` call
 * lives HERE, not in the hook):
 *   - useNotifications                  → listForUser, markAsRead, markAllAsRead, remove
 *   - useServiceProviderData            → listForUserInCompany, markAsRead
 *   - useNotificationPreferences        → getUserPreferences, setPreferences
 *   - useNotificationSends              → listSends, subscribeSends
 *   - useNotificationTemplates          → listTemplates, upsertTemplate, resetTemplate
 *   - useCompanyChannelSettings         → listCompanyChannelSettings, setCompanyChannelSetting
 *   - useCompanyEventConfig             → listCompanyEventConfig, setCompanyEventConfig
 *   - useCompanyAudienceConfig          → listAudienceConfig, setAudienceConfig
 *   - useCompanyNotificationRecipient   → getCompanyRecipient, upsertCompanyRecipient
 *   - useCompanyHatifCredentials        → getCompanyHatifStatus
 *   - useCompanyAvailableChannels       → getCompanyAvailableChannels
 *
 * Auth / RLS: every query is scoped by `company_id` (or `user_id` for the
 * per-user prefs); RLS is the authoritative gate. The hook-level
 * `enabled: !!companyId` / `!!userId` is a UX guard that prevents the query
 * from firing before auth resolves, NOT a security boundary.
 */

import { supabase } from '@/integrations/supabase/client';
import type { Database, Tables } from '@/integrations/supabase/types';
import type {
  NotificationAudience,
  NotificationChannel,
  NotificationEventKey,
  NotificationLocale,
} from '@dallali/notifications';

// ── Row aliases ─────────────────────────────────────────────────────────────

export type NotificationRow = Tables<'notifications'>;
export type NotificationPreferencesRow = Tables<'notification_preferences'>;
export type NotificationSendsRow = Tables<'notification_sends'>;
export type NotificationTemplateRow = Tables<'notification_templates'>;
export type CompanyChannelSettingsRow = Tables<'company_channel_settings'>;
export type CompanyNotificationEventConfigRow =
  Tables<'company_notification_event_config'>;
export type NotificationAudienceConfigRow =
  Tables<'notification_audience_config'>;
export type CompanyNotificationRecipientRow =
  Tables<'company_notification_recipients'>;

// ── RPC result shapes ───────────────────────────────────────────────────────

// Mirrors `get_company_hatif_status` (raw row); the hook still maps snake → camel.
export type CompanyHatifStatusRow =
  Database['public']['Functions']['get_company_hatif_status']['Returns'][number];

// Mirrors `company_available_channels` (raw row); the hook narrows `channel`
// through `isNotificationChannel` before keying into the AvailabilityMap.
export type CompanyAvailableChannelRow =
  Database['public']['Functions']['company_available_channels']['Returns'][number];

// ── Method input shapes ─────────────────────────────────────────────────────

export interface PreferenceCellRef {
  eventKey: NotificationEventKey;
  channel: NotificationChannel;
}

export interface SetPreferencesInput {
  userId: string;
  cells: readonly PreferenceCellRef[];
  enabled: boolean;
}

export interface ListSendsParams {
  companyId: string;
  eventKey?: NotificationEventKey;
  channel?: NotificationChannel;
  recipientUserId?: string;
  /** Defaults to 50; hard-capped at 200. */
  limit?: number;
}

export interface SubscribeSendsParams {
  companyId: string;
  onInsert: () => void;
}

export interface ListTemplatesParams {
  companyId: string;
}

export interface UpsertTemplateInput {
  companyId: string;
  eventKey: NotificationEventKey;
  audience: NotificationAudience;
  channel: NotificationChannel;
  locale: NotificationLocale;
  subject: string | null;
  bodyText: string;
  bodyHtml: string | null;
}

export interface ResetTemplateInput {
  companyId: string;
  eventKey: NotificationEventKey;
  audience: NotificationAudience;
  channel: NotificationChannel;
  locale: NotificationLocale;
}

export interface SetCompanyChannelSettingInput {
  companyId: string;
  channel: NotificationChannel;
  enabled: boolean;
  /** Auth user id of the super-admin making the change; nullable when unknown. */
  updatedBy: string | null;
}

/** One cell of the audience × channel matrix for a given event. */
export interface AudienceCellRef {
  eventKey: NotificationEventKey;
  audience: NotificationAudience;
  channel: NotificationChannel;
}

export interface SetAudienceConfigInput {
  companyId: string;
  cells: readonly AudienceCellRef[];
  enabled: boolean;
  /** Auth user id of the company admin making the change; null when unknown. */
  updatedBy: string | null;
}

export interface UpsertCompanyRecipientInput {
  companyId: string;
  /** Recipient role discriminant — 'finance' is the only one wired today. */
  role: string;
  name: string;
  email: string | null;
  phone: string | null;
  whatsappTo: string | null;
  locale: NotificationLocale;
  enabled: boolean;
}

export interface SetCompanyEventConfigInput {
  companyId: string;
  eventKey: NotificationEventKey;
  enabled: boolean;
  /** Auth user id of the company-admin making the change; nullable when unknown. */
  updatedBy: string | null;
}

// ── Defaults for the limit clamp ────────────────────────────────────────────

const SENDS_DEFAULT_LIMIT = 50;
const SENDS_MAX_LIMIT = 200;

/**
 * Clamp the optional `limit` to [1, 200] with a default of 50. Hoisted so it
 * is testable independently of the listSends() method.
 */
export function clampSendsLimit(limit: number | undefined): number {
  return Math.min(limit ?? SENDS_DEFAULT_LIMIT, SENDS_MAX_LIMIT);
}

// ── Repository ──────────────────────────────────────────────────────────────

export const notificationsRepository = {
  // ── notifications (in-app feed) ───────────────────────────────────────────

  /**
   * The signed-in user's in-app feed, newest first. User-scoped (not
   * company-scoped) — the bell shows the user's notifications across the
   * account; RLS restricts rows to the caller.
   */
  async listForUser(userId: string, limit = 50): Promise<NotificationRow[]> {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return (data ?? []) as NotificationRow[];
  },

  /**
   * Company-scoped variant used by the service-provider portal (same feed,
   * additionally pinned to the provider's company).
   */
  async listForUserInCompany(
    userId: string,
    companyId: string,
    limit = 50
  ): Promise<NotificationRow[]> {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return (data ?? []) as NotificationRow[];
  },

  /** Mark one notification read, scoped to the company. */
  async markAsRead(notificationId: string, companyId: string): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notificationId)
      .eq('company_id', companyId);
    if (error) throw error;
  },

  /** Mark every unread notification for the user read, scoped to the company. */
  async markAllAsRead(userId: string, companyId: string): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', userId)
      .eq('company_id', companyId)
      .eq('is_read', false);
    if (error) throw error;
  },

  /** Delete one notification, scoped to the company. */
  async remove(notificationId: string, companyId: string): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId)
      .eq('company_id', companyId);
    if (error) throw error;
  },

  // ── notification_preferences ──────────────────────────────────────────────

  /**
   * Pivoted per-user notification preferences. The dispatcher and the
   * settings UI both consume the raw rows — the hook collapses them into a
   * NotificationPreferencesMap via `buildPreferencesMap`.
   */
  async getUserPreferences(
    userId: string
  ): Promise<NotificationPreferencesRow[]> {
    const { data, error } = await supabase
      .from('notification_preferences')
      .select('*')
      .eq('user_id', userId);
    if (error) throw error;
    return (data ?? []) as NotificationPreferencesRow[];
  },

  /**
   * Upsert N (user, event, channel) preferences in ONE round-trip. The composite key
   * `user_id,event_key,channel` matches the DB unique constraint.
   */
  async setPreferences(input: SetPreferencesInput): Promise<void> {
    if (input.cells.length === 0) return;
    const now = new Date().toISOString();
    const { error } = await supabase.from('notification_preferences').upsert(
      input.cells.map((cell) => ({
        user_id: input.userId,
        event_key: cell.eventKey,
        channel: cell.channel,
        enabled: input.enabled,
        updated_at: now,
      })),
      { onConflict: 'user_id,event_key,channel' }
    );
    if (error) throw error;
  },

  // ── notification_sends ────────────────────────────────────────────────────

  /**
   * Company-scoped delivery log, newest first. The optional filters
   * (eventKey / channel / recipientUserId) are applied as additional `.eq`s
   * AFTER `.limit(...)`, matching the hook's current chain so the produced
   * SQL is byte-identical.
   */
  async listSends(params: ListSendsParams): Promise<NotificationSendsRow[]> {
    const limit = clampSendsLimit(params.limit);
    let q = supabase
      .from('notification_sends')
      .select('*')
      .eq('company_id', params.companyId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (params.eventKey) q = q.eq('event_key', params.eventKey);
    if (params.channel) q = q.eq('channel', params.channel);
    if (params.recipientUserId)
      q = q.eq('recipient_user_id', params.recipientUserId);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []) as NotificationSendsRow[];
  },

  /**
   * Subscribe to realtime INSERTs on `notification_sends` scoped to the
   * company. Returns an unsubscribe function that the caller must invoke
   * in its useEffect cleanup. The channel name and the filter format mirror
   * the hook's original implementation so an in-flight realtime session
   * survives the refactor.
   */
  subscribeSends(params: SubscribeSendsParams): () => void {
    const channel = supabase
      .channel(`notification-sends-${params.companyId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notification_sends',
          filter: `company_id=eq.${params.companyId}`,
        },
        () => {
          params.onInsert();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  },

  // ── notification_templates ────────────────────────────────────────────────

  /**
   * Company + platform-default templates in one query. The platform default
   * is the row with `company_id IS NULL`; the consuming hook collapses the
   * pair to one resolved template per (event, audience, channel, locale)
   * with the company row winning.
   *
   * RLS gates this too, but filtering in-query is belt-and-braces: a
   * misconfigured policy would otherwise leak rows from other companies.
   */
  async listTemplates(
    params: ListTemplatesParams
  ): Promise<NotificationTemplateRow[]> {
    const { data, error } = await supabase
      .from('notification_templates')
      .select('*')
      .or(`company_id.eq.${params.companyId},company_id.is.null`);
    if (error) throw error;
    return (data ?? []) as NotificationTemplateRow[];
  },

  /**
   * Upsert a company-scoped template override. The composite key
   * `company_id,event_key,audience,channel,locale` matches the DB unique
   * constraint. Always sets `enabled: true` (the platform default row is
   * what gates dispatch when the override is absent).
   */
  async upsertTemplate(input: UpsertTemplateInput): Promise<void> {
    const { error } = await supabase.from('notification_templates').upsert(
      {
        company_id: input.companyId,
        event_key: input.eventKey,
        audience: input.audience,
        channel: input.channel,
        locale: input.locale,
        subject: input.subject,
        body_text: input.bodyText,
        body_html: input.bodyHtml,
        enabled: true,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'company_id,event_key,audience,channel,locale' }
    );
    if (error) throw error;
  },

  /**
   * Delete the company override so the platform default takes over again.
   * Scoped by the full composite key — the platform-default row (NULL
   * company_id) is unaffected.
   */
  async resetTemplate(input: ResetTemplateInput): Promise<void> {
    const { error } = await supabase
      .from('notification_templates')
      .delete()
      .eq('company_id', input.companyId)
      .eq('event_key', input.eventKey)
      .eq('audience', input.audience)
      .eq('channel', input.channel)
      .eq('locale', input.locale);
    if (error) throw error;
  },

  // ── company_channel_settings (super-admin per-company channel on/off) ─────

  /**
   * Super-admin per-company channel on/off. The hook keeps its `defaults`
   * fallback (inapp+email ON, sms+whatsapp OFF) for missing rows.
   */
  async listCompanyChannelSettings(
    companyId: string
  ): Promise<Pick<CompanyChannelSettingsRow, 'channel' | 'enabled'>[]> {
    const { data, error } = await supabase
      .from('company_channel_settings')
      .select('channel,enabled')
      .eq('company_id', companyId);
    if (error) throw error;
    return (data ?? []) as Pick<
      CompanyChannelSettingsRow,
      'channel' | 'enabled'
    >[];
  },

  /**
   * Upsert one (company, channel) row. Composite key `company_id,channel`.
   */
  async setCompanyChannelSetting(
    input: SetCompanyChannelSettingInput
  ): Promise<void> {
    const { error } = await supabase.from('company_channel_settings').upsert(
      {
        company_id: input.companyId,
        channel: input.channel,
        enabled: input.enabled,
        updated_by: input.updatedBy,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'company_id,channel' }
    );
    if (error) throw error;
  },

  // ── company_notification_event_config (company-admin per-event on/off) ────

  /**
   * Company-admin per-event on/off. Missing row = enabled, so the consuming
   * hook treats absence as `true`.
   */
  async listCompanyEventConfig(
    companyId: string
  ): Promise<
    Pick<CompanyNotificationEventConfigRow, 'event_key' | 'enabled'>[]
  > {
    const { data, error } = await supabase
      .from('company_notification_event_config')
      .select('event_key,enabled')
      .eq('company_id', companyId)
      .returns<
        Pick<CompanyNotificationEventConfigRow, 'event_key' | 'enabled'>[]
      >();
    if (error) throw error;
    return data ?? [];
  },

  /**
   * Upsert one (company, event) row. Composite key `company_id,event_key`.
   */
  async setCompanyEventConfig(
    input: SetCompanyEventConfigInput
  ): Promise<void> {
    const { error } = await supabase
      .from('company_notification_event_config')
      .upsert(
        {
          company_id: input.companyId,
          event_key: input.eventKey,
          enabled: input.enabled,
          updated_by: input.updatedBy,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'company_id,event_key' }
      );
    if (error) throw error;
  },

  // ── notification_audience_config (company-admin per audience × channel) ───

  /**
   * Company-admin per (event, audience, channel) on/off.
   *
   * Reads BOTH tiers: the company's own overrides and the dense platform
   * defaults (`company_id IS NULL`), because the settings UI has to render the
   * inherited default for every cell the company never touched. The consuming
   * hook folds company-over-platform per cell.
   */
  async listAudienceConfig(
    companyId: string
  ): Promise<
    Pick<
      NotificationAudienceConfigRow,
      'company_id' | 'event_key' | 'audience' | 'channel' | 'enabled'
    >[]
  > {
    const { data, error } = await supabase
      .from('notification_audience_config')
      .select('company_id,event_key,audience,channel,enabled')
      .or(`company_id.eq.${companyId},company_id.is.null`)
      .returns<
        Pick<
          NotificationAudienceConfigRow,
          'company_id' | 'event_key' | 'audience' | 'channel' | 'enabled'
        >[]
      >();
    if (error) throw error;
    return data ?? [];
  },

  /**
   * Upsert N (company, event, audience, channel) cells in ONE round-trip.
   *
   * Always an array: a single switch is one cell, an audience row master is
   * four, and "silence employees everywhere" is a couple of dozen — one code
   * path, one request, one optimistic patch, one rollback.
   *
   * Conflict target is `uq_notification_audience_config` — a total
   * UNIQUE NULLS NOT DISTINCT constraint over these four columns. It must
   * stay total: PostgREST can only name columns as the ON CONFLICT arbiter,
   * so a partial unique index here fails every upsert with 42P10
   * (DALLALI-FRONTEND-FX). Company admins can only ever write their own tier
   * (RLS enforces `company_id IS NOT NULL`), so the platform defaults stay
   * untouched.
   */
  async setAudienceConfig(input: SetAudienceConfigInput): Promise<void> {
    if (input.cells.length === 0) return;
    const now = new Date().toISOString();
    const { error } = await supabase
      .from('notification_audience_config')
      .upsert(
        input.cells.map((cell) => ({
          company_id: input.companyId,
          event_key: cell.eventKey,
          audience: cell.audience,
          channel: cell.channel,
          enabled: input.enabled,
          updated_by: input.updatedBy,
          updated_at: now,
        })),
        { onConflict: 'company_id,event_key,audience,channel' }
      );
    if (error) throw error;
  },

  // ── company_notification_recipients (per-company contact per role) ────────

  /**
   * The company's contact for one recipient role (finance today). Returns null
   * when never configured — which is the common case, and the reason the
   * dispatcher's finance fan-out silently reached nobody before this surface
   * existed. Deliberately NOT filtered on `enabled`: the settings form has to
   * show and be able to re-enable a disabled row.
   */
  async getCompanyRecipient(
    companyId: string,
    role: string
  ): Promise<CompanyNotificationRecipientRow | null> {
    const { data, error } = await supabase
      .from('company_notification_recipients')
      .select('*')
      .eq('company_id', companyId)
      .eq('role', role)
      .maybeSingle();
    if (error) throw error;
    return data ?? null;
  },

  /** Upsert the company's contact for a role. Key `company_id,role`. */
  async upsertCompanyRecipient(
    input: UpsertCompanyRecipientInput
  ): Promise<void> {
    const { error } = await supabase
      .from('company_notification_recipients')
      .upsert(
        {
          company_id: input.companyId,
          role: input.role,
          name: input.name,
          email: input.email,
          phone: input.phone,
          whatsapp_to: input.whatsappTo,
          locale: input.locale,
          enabled: input.enabled,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'company_id,role' }
      );
    if (error) throw error;
  },

  // ── Hatif credentials status RPC (super-admin) ────────────────────────────

  /**
   * Non-secret status of a company's Hatif WhatsApp credentials. The RPC is
   * super-admin-guarded in SQL; the secret itself is never returned — only
   * whether it is configured. Returns the first row, or null if the RPC
   * yielded no rows (e.g. no credentials configured yet).
   */
  async getCompanyHatifStatus(
    companyId: string
  ): Promise<CompanyHatifStatusRow | null> {
    const { data, error } = await supabase.rpc('get_company_hatif_status', {
      p_company_id: companyId,
    });
    if (error) throw error;
    const rows = (data ?? []) as CompanyHatifStatusRow[];
    return rows[0] ?? null;
  },

  // ── company_available_channels RPC ────────────────────────────────────────

  /**
   * Resolved per-channel availability for the company: a channel is
   * `available` iff globally enabled AND company-enabled AND configured
   * (e.g. WhatsApp needs Hatif creds). Shared source of truth with the Nest
   * dispatcher. Returns the rows; the hook normalises against the
   * defaults map.
   */
  async getCompanyAvailableChannels(
    companyId: string
  ): Promise<CompanyAvailableChannelRow[]> {
    const { data, error } = await supabase.rpc('company_available_channels', {
      p_company_id: companyId,
    });
    if (error) throw error;
    return (data ?? []) as CompanyAvailableChannelRow[];
  },
};
